import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Trash2, Loader2 } from 'lucide-react';

/**
 * Row-selection state for multi-select tables.
 * Returns { selected (Set), isSelected, toggle, toggleAll, clear, size }.
 */
export function useBulkSelect() {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Select-all toggles the given page ids: if all are already selected, clear; else select all.
  const toggleAll = useCallback((ids) => {
    setSelected((prev) => {
      const allSelected = ids.length > 0 && ids.every((i) => prev.has(i));
      return allSelected ? new Set() : new Set(ids);
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);
  const isSelected = useCallback((id) => selected.has(id), [selected]);

  return { selected, isSelected, toggle, toggleAll, clear, size: selected.size };
}

/**
 * Floating selection bar for multi-select tables.
 *
 * Every page used to render its "N selected + Delete" strip inline, between the
 * filters and the table — so ticking the first checkbox pushed the whole table
 * down and the row under the cursor moved. This floats over the page instead:
 * nothing in the document flow changes, and the bar stays reachable however far
 * the list is scrolled.
 *
 * z-40 keeps it BELOW dialogs (z-50), so a confirm/bulk-edit dialog opened while
 * rows are selected still covers it. The outer layer is click-through so it never
 * steals clicks from the page behind it.
 *
 * Pass the actions as children (delete, bulk edit, mark left, ...).
 */
export function BulkSelectionBar({
  count,
  label = 'selected',
  onClear,
  children,
  testId = 'bulk-selection-bar',
}) {
  if (!count) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4 pointer-events-none"
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      <div className="pointer-events-auto flex items-center gap-2 sm:gap-3 rounded-full border border-[#D8D9E8] bg-white px-3 sm:px-4 py-2 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-200">
        <span className="text-sm font-medium text-[#1C1F1D] whitespace-nowrap">
          {count} {label}
        </span>
        {onClear && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-[#5C635F]"
            onClick={onClear} data-testid="bulk-clear-selection">
            Clear
          </Button>
        )}
        <span className="h-5 w-px bg-[#D8D9E8] shrink-0" aria-hidden="true" />
        <div className="flex items-center gap-2 flex-wrap justify-end">{children}</div>
      </div>
    </div>
  );
}

/** Admin-only "Delete N selected" button. Renders nothing when count is 0. */
export function BulkDeleteButton({ count, deleting, onClick, label = 'selected' }) {
  if (!count) return null;
  return (
    <Button
      variant="outline"
      className="text-[#C85A40] border-[#C85A40] hover:bg-[#C85A40]/10"
      onClick={onClick}
      disabled={deleting}
      data-testid="bulk-delete-btn"
    >
      {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
      Delete {count} {label}
    </Button>
  );
}
